import { IUser } from 'services/BackendApi';
import { ESortOrder, IPagination } from 'store/common/types';

export interface ITaskCategory {
  id: string;
  name: string;
}

export type ITaskUser = Pick<IUser, 'uuid' | 'firstName' | 'lastName'>;

export interface ITaskTeam {
  id: string;
  name: string;
}

export interface ITaskPerson {
  id: string;
  type: string;
  email: string;
  firstName: string;
  lastName: string;
}

export interface ITaskAPI {
  id: string;
  key: string;
  subject: string;
  description: string;
  status: ETaskStatus;
  priority: ETaskPriority;
  startBy: Date;
  deadline: Date;
  category: {
    id: string;
    name: string;
  };
  team: {
    id: string;
    name: string;
  };
  assignee: ITaskPerson;
  reviewer: ITaskPerson | null;
  bookingRef: string;
  booking: {
    id: string;
    humanReadableId: string;
    leadGuestFirstName: string;
    leadGuestLastName: string;
  } | null,
  createdBy: ITaskPerson;
  createdAt: string;
  updatedBy: ITaskPerson | null;
  updatedAt: string;
  reminders: ITaskReminder[]
  crmDirectoryEntryId: string | null;
  crmDirectoryEntryFullName: string | null;
  restrictedToTeams: ITaskTeam[];
  /** Format: uuid. The recurring task this task was created from, `null` for a task created by hand. */
  recurringTaskId: string | null;
}

export interface ISearchTasksResponse {
  tasks: ITaskAPI[];
  pagination: IPagination;
  count: number;
}

export interface ITask {
  id: string;
  key: string;
  subject: string;
  description: string;
  status: ETaskStatus;
  priority: ETaskPriority;
  startBy: Date;
  deadline: Date;
  category: {
    id: string;
    name: string;
  };
  team: {
    id: string;
    name: string;
  };
  assignee: ITaskPerson;
  reviewer: ITaskPerson | null;
  bookingRef?: string;
  booking?: {
    id: string;
    humanReadableId: string;
    leadGuestFirstName: string;
    leadGuestLastName: string;
  } | null,
  reporter: ITaskPerson;
  createdAt: string;
  updatedBy: ITaskPerson | null;
  updatedAt: string | null;
  reminders: ITaskReminder[];
  crmDirectoryEntryId: string | null;
  crmDirectoryEntryFullName: string | null;
  restrictedToTeams: ITaskTeam[];
  recurringTaskId: string | null;
}

export enum ETasksSortableKey {
  KEY = 'key',
  SUBJECT = 'subject',
  STATUS = 'status',
  PRIORITY = 'priority',
  DEADLINE = 'deadline',
  CATEGORY = 'category',
  TEAM = 'team',
  ASSIGNEE = 'assignee',
  REVIEWER = 'reviewer',
  BOOKING_HUMAN_READABLE_ID = 'bookingHumanReadableId',
  REPORTER = 'createdBy',
}

export enum ETaskStatus {
  BACKLOG = 'backlog',
  TO_DO = 'to-do',
  IN_PROGRESS = 'in-progress',
  BLOCKED = 'blocked',
  // TMS rejects this status on a task without a reviewer (REVIEWER_REQUIRED)
  TO_REVIEW = 'to-review',
  DONE = 'done',
}

export enum ETaskPriority {
  LOW = 'low',
  MEDIUM = 'medium',
  HIGH = 'high',
}

export interface ITasksListSort {
  key: ETasksSortableKey;
  order: ESortOrder;
}

// OWA-11992. Leaving `recurring` out of the search filter is the same as ALL.
export enum ETaskRecurringFilter {
  ALL = 'all',
  RECURRING = 'recurring',
  NON_RECURRING = 'non-recurring',
}

export interface ITaskSearchFilter {
  key: string;
  statuses: ETaskStatus[];
  priority?: ETaskPriority;
  deadlineFrom: string;
  deadlineTo: string;
  categoryIds: string[];
  teamId?: string;
  bookingId?: string;
  assigneeId?: string;
  /** Format: uuid. `null` means tasks with no reviewer. */
  reviewerId?: string | null;
  bookingHumanReadableId: string;
  bookingLeadGuestName: string;
  createdById?: string;
  recurring?: ETaskRecurringFilter;
}

export interface ISupplierTaskSearchSupplier {
  // echoed back in the response so results can be matched to suppliers
  supplierCode: string;
  // CRM directory entry uuid or be_id of the supplier company
  directoryEntryId: string;
}

export interface ISupplierTaskSearchRequest {
  bookingId: string;
  suppliers: ISupplierTaskSearchSupplier[];
}

export interface ISupplierContact {
  id: string;
  fullName: string;
}

export interface ISupplierTaskSearchResultItem {
  supplierCode: string;
  contacts: ISupplierContact[];
  tasks: ITaskAPI[];
}

export interface ISupplierTaskSearchResponse {
  suppliers: ISupplierTaskSearchResultItem[];
}

export type TReminderChannel = 'email';
export type TReminderOffsetUnit = 'minute' | 'hour' | 'day' | 'week';

export interface ITaskReminder {
  id: string;
  taskId?: string;
  channel: TReminderChannel;
  offset: number;
  offsetUnit: TReminderOffsetUnit;
}
export interface ICreateTaskRequest {
  subject: string;
  description: string | null;
  status: ETaskStatus;
  priority: ETaskPriority;
  /** Format: date-time */
  startBy: string | null;
  /** Format: date-time */
  deadline: string;
  /** Format: uuid */
  categoryId: string;
  /** Format: uuid */
  teamId: string;
  /** Format: uuid */
  assigneeId: string | null;
  /** Format: uuid. Required by the TMS schema, `null` when there is no reviewer. */
  reviewerId: string | null;
  bookingHumanReadableId: string | null;
  reminders: ITaskReminder[];
  crmDirectoryEntryId: string | null;
  crmDirectoryEntryFullName: string | null;
  /** Format: uuid[]. Empty array means the task is public. */
  restrictedToTeamIds: string[];
};

// OWA-11923 - a recurring task is a template task plus the rule the tasks are created from.
export enum ERecurrenceFrequency {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
  YEAR = 'year',
}

export enum ERecurrenceEndCondition {
  NEVER = 'never',
  AFTER_N = 'after_n',
  UNTIL_DATE = 'until_date',
}

/** Every key is required by the TMS schema; the ones that don't apply to the chosen options are `null`. */
export interface IRecurrenceRequest {
  /** Format: date (YYYY-MM-DD), the user's local calendar day */
  startDate: string;
  frequency: ERecurrenceFrequency;
  interval: number;
  /** Weekly only. ISO days of week, 1 = Monday ... 7 = Sunday. */
  daysOfWeek: number[] | null;
  /** Monthly only, "On day <dayOfMonth>". */
  dayOfMonth: number | null;
  /** Monthly only, with dayOfWeek. 1-5, 5 is the last such weekday of the month. */
  weekOfMonth: number | null;
  /** Monthly only, with weekOfMonth. ISO day of week. */
  dayOfWeek: number | null;
  endCondition: ERecurrenceEndCondition;
  endAfterOccurrences: number | null;
  /** Format: date (YYYY-MM-DD) */
  endDate: string | null;
  deadlineOffsetDays: number;
  leadTimeDays: number;
}

export interface ICreateRecurringTaskRequest {
  subject: string;
  description: string | null;
  priority: ETaskPriority;
  /** Format: uuid */
  categoryId: string;
  /** Format: uuid */
  teamId: string;
  /** Format: uuid */
  assigneeId: string | null;
  /** Format: uuid */
  reviewerId: string | null;
  reminders: Omit<ITaskReminder, 'id' | 'taskId'>[];
  /** Format: uuid[]. Empty array means the tasks are public. */
  restrictedToTeamIds: string[];
  recurrence: IRecurrenceRequest;
  /** OWA-12096. Copied to every task created from it. On PUT a missing key keeps the saved booking, so send `null` to remove it. */
  bookingHumanReadableId: string | null;
  /** Format: uuid. OWA-12096. Set with crmDirectoryEntryFullName or both `null`, else 400 INCONSISTENT_CONTACT_DATA. */
  crmDirectoryEntryId: string | null;
  crmDirectoryEntryFullName: string | null;
}

export interface IRecurrence extends IRecurrenceRequest {
  /** Format: date */
  nextOccurrenceDate: string | null;
  /** Format: date */
  lastOccurrenceDate: string | null;
  isActive: boolean;
}

// OWA-11997 - PUT v1/recurring-tasks/{taskId}. The whole form, not a diff.
export type IUpdateRecurringTaskRequest = ICreateRecurringTaskRequest;

export interface IRecurringTaskAPI {
  id: string;
  key: string;
  subject: string;
  description: string | null;
  priority: ETaskPriority;
  category: ITaskCategory;
  team: ITaskTeam;
  assignee: ITaskPerson | null;
  reviewer: ITaskPerson | null;
  createdBy: ITaskPerson;
  createdAt: string;
  reminders: ITaskReminder[];
  restrictedToTeams: ITaskTeam[];
  recurrence: IRecurrence;
  /** OWA-12096 */
  booking: ITaskAPI['booking'];
  crmDirectoryEntryId: string | null;
  crmDirectoryEntryFullName: string | null;
  /** OWA-11997. Returned by GET / PUT v1/recurring-tasks/{taskId}. */
  status: ERecurringTaskStatus;
  /** OWA-11998. False once a task was created from it. */
  canDelete: boolean;
}

// OWA-11996 - POST v1/recurring-tasks/search (RecurringTaskSearchRequest / RecurringTaskSearchResponse).
/** inactive when the recurrence has no next occurrence */
export enum ERecurringTaskStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export enum ERecurringTasksSortableKey {
  SUBJECT = 'subject',
  FREQUENCY = 'frequency',
  LAST_OCCURRENCE = 'lastOccurrence',
  NEXT_OCCURRENCE = 'nextOccurrence',
  TEAM = 'team',
  ASSIGNEE = 'assignee',
  REVIEWER = 'reviewer',
  STATUS = 'status',
  UPDATED_AT = 'updatedAt',
}

export interface IRecurringTaskSearchFilter {
  subject?: string;
  /** Format: uuid[] */
  categoryIds?: string[];
  /** Format: uuid */
  teamId?: string;
  frequencies?: ERecurrenceFrequency[];
  status?: ERecurringTaskStatus;
}

export interface IRecurringTaskSearchRequest {
  filter?: IRecurringTaskSearchFilter;
  sort?: {
    key: ERecurringTasksSortableKey;
    order: ESortOrder;
  };
  pagination?: IPagination;
}

/** Like IRecurringTaskAPI without the description, with the recurrence rule flattened. */
export interface IRecurringTaskListItem {
  id: string;
  key: string;
  subject: string;
  priority: ETaskPriority;
  category: ITaskCategory;
  team: ITaskTeam;
  assignee: ITaskPerson | null;
  reviewer: ITaskPerson | null;
  createdBy: ITaskPerson;
  createdAt: string;
  /** The creation date until the recurring task is updated. */
  updatedAt: string;
  restrictedToTeams: ITaskTeam[];
  frequency: ERecurrenceFrequency;
  interval: number;
  /** ISO days of week, 1 = Monday ... 7 = Sunday */
  daysOfWeek: number[] | null;
  dayOfMonth: number | null;
  /** 1-5, 5 is the last such weekday of the month */
  weekOfMonth: number | null;
  /** ISO day of week */
  dayOfWeek: number | null;
  /** Format: date (YYYY-MM-DD) */
  lastOccurrenceDate: string | null;
  /** Format: date (YYYY-MM-DD) */
  nextOccurrenceDate: string | null;
  status: ERecurringTaskStatus;
  /** OWA-11998. False once a task was created from it. */
  canDelete: boolean;
}

export interface IRecurringTaskSearchResponse {
  count: number;
  pagination: IPagination;
  recurringTasks: IRecurringTaskListItem[];
}

export interface ITaskAttachment {
  id: string;
  name: string;
  url: string;
  createdBy: ITaskPerson;
  createdAt: string;
}

export interface ITaskComment {
  id: string;
  content: string;
  createdBy: ITaskPerson;
  createdAt: string;
}

export interface ICreateTaskCommentResponse {
  comment: ITaskComment;
  warnings: string[];
}